import { useTranslation } from "react-i18next";

import { Button, Input, StatusChip, Table, Text } from "@fragments";


import Popper from "@fragments/Popper";
import ManageIntegration from "../features/ManageIntegration";
import IntegrationLogs from "../features/IntegrationLogs";

import { IconDotsVertical, IconPlus } from "@tabler/icons-react";

import { PLATFORMS, getEvents } from "../enums/platforms";
import PlatformLogo from "../components/PlatformLogo";

import useConfigHeader from "@pages/CommunityConfig/hooks/useConfigHeader";
import ConfigHeaderActions from "@pages/CommunityConfig/components/ConfigHeaderActions";
import PlatformCard, { PlatformGrid } from "@pages/CommunityConfig/components/PlatformCard";

import './styles.scss';


function IntegrationStatus({ item, onToggle }) {
    const { t } = useTranslation("integrations");
    const label = !item.active
        ? t('common:status.inactive')
        : item.direction === 'inbound' ? t('list.direction.inbound') : t('list.direction.outbound');

    return (
        <>
            <Text.span contrast={6} left={
                <div className={`live-indicator${!item.active ? ' live-indicator--off' : ''}`} />
            }>{label}</Text.span>
            <Input.Switch
                value={item.active}
                onChange={() => onToggle(item)}
            />
        </>
    );
}


export default function IntegrationsView({
    loading,
    communityLoading,
    community,
    integrations,
    pagination,
    popperRef,
    manageModalRef,
    logsModalRef,
    handleLoad,
    handleToggle,
    handleOpenCreate,
    handleOpenOptions,
}) {
    const { t } = useTranslation("integrations");
    const EVENTS = getEvents(t);

    // Page identity lives in the floating settings header. The page now holds
    // both the available platforms and the connected integrations, so the
    // title falls back to the sidebar entry ("Plataformas externas", with its
    // icon); the create action is portaled into it below.
    useConfigHeader({ subtitle: t('list.subtitle') });

    const columns = [
        {
            id: 'platform-logo',
            title: '',
            className: 'pl-1',
            loadingWidth: 80,
            allowReorder: false,
            customRender: (item) => (
                <PlatformLogo platformKey={item.platform} style={{ width: '32px', height: '32px', marginLeft: '8px' }} />
            ),
        },
        {
            id: 'plan',
            title: t('common:entities.plan'),
            grow: 1,
            loadingWidth: 120,
            customRender: (item) => {
                const plan = community?.plans?.find(
                    (p) => p.id == item.plan_id || p.id == item.plan
                );
                return <Text.span contrast={7}>{plan?.label ?? t('list.allPlans')}</Text.span>;
            },
        },
        {
            id: 'name',
            title: t('list.columns.name'),
            grow: 2,
            loadingWidth: 120,
            customRender: (item) => {
                return <Text.span contrast={7}>{item.name}</Text.span>;
            },
        },
        {
            id: 'event',
            title: t('list.columns.event'),
            align: 'center',
            className: 'integration-evento-col',
            loadingWidth: 160,
            customRender: (item) => (
                <StatusChip.Default text={EVENTS[item.event]} />
            ),
        },
        {
            id: 'status',
            title: t('common:fields.status'),
            align: 'center',
            display: 'row',
            loadingWidth: 130,
            customRender: (item) => (
                <IntegrationStatus item={item} onToggle={handleToggle} />
            ),
        },
    ];


    return (
        <div className="">
            <ConfigHeaderActions>
                <Button
                    text={t('actions.create')}
                    endIcon={<IconPlus />}
                    onClick={() => handleOpenCreate()}
                    color="inverse"
                    size="small"
                />
            </ConfigHeaderActions>

            <div className="mt-4 px-4">
                {/* Available platforms — "+" opens the create modal with the
                    platform preselected. */}
                <Text.h5 weight={500} contrast={9} className="mb-2">{t('available.title')}</Text.h5>
                <PlatformGrid>
                    {Object.values(PLATFORMS).map((platform) => (
                        <PlatformCard
                            key={platform.key}
                            logo={platform.logo}
                            icon={platform.icon}
                            name={platform.name}
                            description={t(`platforms.${platform.key}.description`)}
                            addLabel={t('actions.create')}
                            onAdd={() => handleOpenCreate(platform.key)}
                        />
                    ))}
                </PlatformGrid>

                <Text.h5 weight={500} contrast={9} className="mt-4 mb-2">{t('list.title')}</Text.h5>
                <Table
                    id="community-integrations"
                    columns={columns}
                    data={integrations}
                    loading={loading || communityLoading}
                    contained={true}
                    emptyMessage={t('list.emptyMessage')}
                    emptySubtitle={t('list.emptySubtitle')}
                    enablePagination={true}
                    pages={pagination.pages}
                    records={pagination.records}
                    page={pagination.page}
                    onSelectPage={(page) => handleLoad(page)}
                    actionsComponent={(item) => (
                        <Button
                            color="overlay"
                            size="small"
                            icon={<IconDotsVertical size={16} />}
                            onClick={(e) => handleOpenOptions(e, item)}
                        />
                    )}
                />

            </div>

            <Popper controlRef={popperRef} />

            <ManageIntegration modalRef={manageModalRef} community={community} />
            <IntegrationLogs modalRef={logsModalRef} />
        </div>
    );
}
